import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { maintenanceApi } from '@/api/maintenance.api';
import { getAllMachines } from '@/api/machine.api';
import { getAllOperators } from '@/api/operators.api';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface ChecklistItem {
  id: string;
  taskName: string;
  description: string;
  requiresSpares: boolean;
  spareParts: Array<{ itemName: string; qty: number; unit: string }>;
}

export default function MaintenanceScheduleNewPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const preSelectedMachine = location.state?.machine;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Master Options
  const [machines, setMachines] = useState<Array<{ id: string; name: string }>>([]);
  const [operators, setOperators] = useState<Array<{ id: string; name: string }>>([]);
  const [dbSpareParts, setDbSpareParts] = useState<Array<any>>([]);

  // Form State
  const [machineId, setMachineId] = useState('');
  const [machineName, setMachineName] = useState('');
  const [scheduleType, setScheduleType] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL'>('MONTHLY');
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<number>(30);
  const [assignedToId, setAssignedToId] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [advanceAlertDays, setAdvanceAlertDays] = useState<number>(7);
  const [nextDueDate, setNextDueDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Inspection Checklist Builder
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    {
      id: 'task_1',
      taskName: 'Daily Pre-shift Safety & Lubrication Inspection',
      description: 'Check oil sight glass and apply grease to guide pillars',
      requiresSpares: false,
      spareParts: []
    }
  ]);

  useEffect(() => {
    async function loadMasterData() {
      try {
        const [macRes, opRes, spRes] = await Promise.all([
          getAllMachines().catch(() => ({ data: [] })),
          getAllOperators().catch(() => ({ data: [] })),
          maintenanceApi.getSpareParts().catch(() => [])
        ]);

        if (macRes?.data && Array.isArray(macRes.data)) {
          setMachines(macRes.data.map((m: any) => ({ id: m.id, name: m.name })));
        }
        if (opRes?.data && Array.isArray(opRes.data)) {
          setOperators(opRes.data.map((o: any) => ({ id: o.id, name: o.name })));
        }
        if (Array.isArray(spRes)) {
          setDbSpareParts(spRes);
        }
      } catch (err) {
        console.error('Failed to load master options:', err);
      }
    }
    loadMasterData();
  }, []);

  useEffect(() => {
    if (preSelectedMachine) {
      if (preSelectedMachine.id) setMachineId(preSelectedMachine.id);
      if (preSelectedMachine.name) setMachineName(preSelectedMachine.name);
    }
  }, [preSelectedMachine]);

  const machineOptionsList: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Target Machine --' },
    ...machines.map((m) => ({ value: m.id, label: m.name }))
  ], [machines]);

  const frequencyOptions: SearchableOption[] = useMemo(() => [
    { value: 'DAILY', label: 'DAILY (Every Day)' },
    { value: 'WEEKLY', label: 'WEEKLY (Every 7 Days)' },
    { value: 'MONTHLY', label: 'MONTHLY (Every 30 Days)' },
    { value: 'QUARTERLY', label: 'QUARTERLY (Every 3 Months)' },
    { value: 'ANNUAL', label: 'ANNUAL (Every Year)' }
  ], []);

  const operatorOptionsList: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Assignee --' },
    ...operators.map((o) => ({ value: o.id, label: o.name }))
  ], [operators]);

  const sparePartOptionsList: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Spare Part from Inventory --' },
    ...dbSpareParts.map((part) => ({
      value: part.id,
      label: `${part.partCode} — ${part.partName} (${part.stockQty} ${part.unit} in stock - ₹${part.unitCost})`
    }))
  ], [dbSpareParts]);

  const handleSave = async () => {
    if (!machineName.trim()) {
      setError('Please select a target machine');
      return;
    }
    if (!taskName.trim()) {
      setError('Servicing task title is required');
      return;
    }
    if (!nextDueDate) {
      setError('First servicing due date is required');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = {
        machine_id: machineId || null,
        machine_name: machineName,
        schedule_type: scheduleType,
        task_name: taskName.trim(),
        description: description.trim() || '',
        estimated_duration_minutes: Number(duration) || 30,
        assigned_to: assignedToId || null,
        assigned_to_name: assignedToName || 'Technician',
        tasks: checklist,
        next_due_date: nextDueDate,
        advance_alert_days: Number(advanceAlertDays) || 7
      };

      await maintenanceApi.createSchedule(payload);
      toast.success('Maintenance schedule created & first MWO auto-generated!');
      navigate('/manufacturing/maintenance');
    } catch (err: any) {
      console.error('Error creating schedule:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full w-full">
        {/* Clean Header: Back Arrow + Page Title (No breadcrumb, No top buttons) */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/manufacturing/maintenance')}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 cursor-pointer shadow-xs transition-colors"
            title="Back to Maintenance"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">Create Maintenance Schedule</h1>
            <p className="text-xs text-slate-500 mt-0.5">Define recurring preventive servicing rules & auto-generate initial MWO</p>
          </div>
        </div>

        {/* Error Alert if any */}
        {error && (
          <div className="mb-5 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium flex items-center gap-2.5 shadow-xs">
            <i className="ri-error-warning-line text-xl text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        {/* ONE SINGLE FULL-WIDTH CARD FOR THE ENTIRE FORM */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-sm space-y-8">
          
          {/* SECTION 1: Machine & Servicing Rule */}
          <div>
            <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
              <i className="ri-tools-line text-blue-600 text-base" />
              Machine & Servicing Rule
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Target Machine <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={machineId}
                  onChange={(selId) => {
                    setMachineId(selId);
                    const found = machines.find((m) => m.id === selId);
                    setMachineName(found ? found.name : selId);
                  }}
                  options={machineOptionsList}
                  placeholder="-- Select Target Machine --"
                  searchPlaceholder="Search machine..."
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Servicing Frequency <span className="text-rose-500">*</span>
                </label>
                <SearchableSelect
                  value={scheduleType}
                  onChange={(val) => setScheduleType(val as any)}
                  options={frequencyOptions}
                  placeholder="Select Frequency"
                />
              </div>

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                    Servicing Task Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Daily Pre-shift Safety & Lubrication Inspection"
                    value={taskName}
                    onChange={(e) => setTaskName(e.target.value)}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                    Estimated Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min={5}
                    value={duration}
                    onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Assignment & Schedule */}
          <div>
            <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
              <i className="ri-user-settings-line text-indigo-600 text-base" />
              Assignment & Execution Schedule
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Assigned Technician / Operator
                </label>
                <SearchableSelect
                  value={assignedToId}
                  onChange={(selId) => {
                    setAssignedToId(selId);
                    const found = operators.find((o) => o.id === selId);
                    setAssignedToName(found ? found.name : selId);
                  }}
                  options={operatorOptionsList}
                  placeholder="-- Select Assignee --"
                  searchPlaceholder="Search assignee..."
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  First Servicing Due Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={nextDueDate}
                  onChange={(e) => setNextDueDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Advance Alert Threshold (Days)
                </label>
                <input
                  type="number"
                  min={0}
                  value={advanceAlertDays}
                  onChange={(e) => setAdvanceAlertDays(parseInt(e.target.value) || 0)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                  placeholder="e.g. 7 days before due date"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
                  Detailed Servicing Instructions & Guidelines
                </label>
                <textarea
                  rows={3}
                  placeholder="Check oil sight glass, inspect safety guards, apply grease to guide pillars..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium"
                />
              </div>
            </div>
          </div>

          {/* SECTION 3: Inspection Checklist & Spares Requirements */}
          <div>
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
              <h2 className="text-sm font-bold text-[#1e293b] uppercase tracking-wider flex items-center gap-2">
                <i className="ri-checkbox-multiple-line text-emerald-600 text-base" />
                Inspection Checklist & Spares Requirements
              </h2>

              <button
                type="button"
                onClick={() => setChecklist([
                  ...checklist,
                  { id: `task_${Date.now()}`, taskName: '', description: '', requiresSpares: false, spareParts: [] }
                ])}
                className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 cursor-pointer inline-flex items-center gap-1.5 transition-colors"
              >
                <i className="ri-add-line text-sm" /> Add Checklist Point
              </button>
            </div>

            <div className="space-y-4">
              {checklist.map((item, idx) => (
                <div key={item.id} className="bg-slate-50/80 p-4 rounded-xl border border-slate-200 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 rounded-lg bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      placeholder="Check point title (e.g. Check hydraulic oil level and pressure)..."
                      value={item.taskName}
                      onChange={(e) => {
                        const updated = [...checklist];
                        updated[idx].taskName = e.target.value;
                        setChecklist(updated);
                      }}
                      className="flex-1 px-3.5 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 font-medium bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (checklist.length === 1) return;
                        setChecklist(checklist.filter((_, i) => i !== idx));
                      }}
                      className="w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center cursor-pointer transition-colors"
                      title="Delete Check Point"
                    >
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>

                  {/* Requires Spares Toggle */}
                  <div className="flex items-center justify-between text-xs pt-2 border-t border-slate-200/60">
                    <label className="flex items-center gap-2 text-slate-700 cursor-pointer font-semibold select-none">
                      <input
                        type="checkbox"
                        checked={item.requiresSpares}
                        onChange={(e) => {
                          const updated = [...checklist];
                          updated[idx].requiresSpares = e.target.checked;
                          if (e.target.checked && updated[idx].spareParts.length === 0) {
                            updated[idx].spareParts = [{ itemName: 'Machine Lubricant / Oil', qty: 1, unit: 'Ltr' }];
                          }
                          setChecklist(updated);
                        }}
                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                      />
                      <span>Requires Spare Parts / Consumables for this task point</span>
                    </label>

                    {item.requiresSpares && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [...checklist];
                          updated[idx].spareParts.push({ itemName: '', qty: 1, unit: 'Pcs' });
                          setChecklist(updated);
                        }}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer inline-flex items-center gap-1"
                      >
                        <i className="ri-add-line" /> Add Spare Item
                      </button>
                    )}
                  </div>

                  {/* Spare Parts Rows */}
                  {item.requiresSpares && item.spareParts.length > 0 && (
                    <div className="space-y-2 pt-1">
                      {item.spareParts.map((sp: any, sIdx: number) => (
                        <div key={sIdx} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white p-2.5 rounded-lg border border-slate-200">
                          <div className="flex-1">
                            {dbSpareParts.length > 0 ? (
                              <SearchableSelect
                                value={sp.sparePartId || sp.itemName}
                                onChange={(selVal) => {
                                  const found = dbSpareParts.find((p) => p.id === selVal || p.partName === selVal);
                                  const updated = [...checklist];
                                  if (found) {
                                    updated[idx].spareParts[sIdx] = {
                                      ...updated[idx].spareParts[sIdx],
                                      sparePartId: found.id,
                                      itemName: found.partName,
                                      unit: found.unit || 'Pcs',
                                      unitCost: found.unitCost || 0
                                    };
                                  } else {
                                    updated[idx].spareParts[sIdx] = {
                                      ...updated[idx].spareParts[sIdx],
                                      itemName: selVal
                                    };
                                  }
                                  setChecklist(updated);
                                }}
                                options={sparePartOptionsList}
                                placeholder="-- Select Spare Part from Inventory --"
                                searchPlaceholder="Search spare part..."
                              />
                            ) : (
                              <input
                                type="text"
                                placeholder="Spare item name (e.g. 6204 Bearing / Oil Filter)..."
                                value={sp.itemName}
                                onChange={(e) => {
                                  const updated = [...checklist];
                                  updated[idx].spareParts[sIdx].itemName = e.target.value;
                                  setChecklist(updated);
                                }}
                                className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none font-medium"
                              />
                            )}
                          </div>
                          <div className="w-24">
                            <input
                              type="number"
                              placeholder="Qty"
                              min={1}
                              value={sp.qty}
                              onChange={(e) => {
                                const updated = [...checklist];
                                updated[idx].spareParts[sIdx].qty = parseFloat(e.target.value) || 1;
                                setChecklist(updated);
                              }}
                              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none font-medium text-center"
                            />
                          </div>
                          <div className="w-24">
                            <input
                              type="text"
                              placeholder="Unit"
                              value={sp.unit || 'Pcs'}
                              onChange={(e) => {
                                const updated = [...checklist];
                                updated[idx].spareParts[sIdx].unit = e.target.value;
                                setChecklist(updated);
                              }}
                              className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none font-medium text-center"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...checklist];
                              updated[idx].spareParts.splice(sIdx, 1);
                              if (updated[idx].spareParts.length === 0) updated[idx].requiresSpares = false;
                              setChecklist(updated);
                            }}
                            className="text-slate-400 hover:text-rose-600 p-1 cursor-pointer"
                          >
                            <i className="ri-close-line text-lg" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Actions Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={() => navigate('/manufacturing/maintenance')}
              className="px-4 py-2 text-sm font-semibold text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-100 bg-white cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              onClick={handleSave}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#2563eb] hover:bg-blue-700 rounded-lg cursor-pointer disabled:opacity-50 inline-flex items-center gap-2 shadow-sm transition-colors"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin text-base" />
              ) : (
                <i className="ri-save-line text-base" />
              )}
              Save Schedule & Auto-Generate MWO
            </button>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
